import { useEffect, useRef, useState } from 'react';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';

import { useDuplexerSubscription } from './use-duplexer-subscription';
import { TURN_STALE_AFTER_MS, shouldApplyFrame, signalUpdatedAt, turnActive, type TurnActivitySignal } from './turn-activity';

interface ConversationPush extends TurnActivitySignal {
  id?: string;
}

/**
 * Whether a turn the USER didn't start is running in `conversationId` — the busy state for a pack
 * accept, a pulse, a brief or an agent report. Seeded by `initial` (the conversation's state at load
 * time, from the messages route) and kept live off the CONVERSATION duplexer channel, which the
 * harness republishes on every iteration.
 *
 * The stamp expires (see `turnActive`), so the hook also re-evaluates on a timer: without it a turn
 * that dies uncleanly publishes nothing further and the indicator, having been switched on by a real
 * event, would never switch off.
 */
export function useRemoteTurn(conversationId: string | null, initial?: TurnActivitySignal | null): boolean {
  const [signal, setSignal] = useState<TurnActivitySignal | null>(initial ?? null);
  const [active, setActive] = useState<boolean>(() => turnActive(initial));
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  // Applied on a conversation switch — the old thread's running turn must not paint a spinner on the
  // new one — and again whenever the seed's CONTENT changes, since the messages fetch that carries it
  // lands after the switch. Keyed on the values rather than the object, which is new every render.
  // The newest frame applied for the OPEN conversation, so an out-of-order older one is ignored rather
  // than clearing a live turn (see `shouldApplyFrame`). Reset with the conversation.
  const lastAppliedAtRef = useRef<number | null>(null);

  const seedKey = `${conversationId ?? ''}|${initial?.pendingPredictionId ?? ''}|${String(initial?.updatedDate ?? '')}`;
  useEffect(() => {
    lastAppliedAtRef.current = signalUpdatedAt(initial);
    setSignal(initial ?? null);
    setActive(turnActive(initial));
  }, [seedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useDuplexerSubscription<ConversationPush>(IndividualsDuplexerChannel.CONVERSATION, (payload) => {
    const entity = payload.entity;
    const id = entity?.id ?? payload.entityId;
    if (!entity || !id || id !== conversationIdRef.current) return;
    const frameAt = signalUpdatedAt(entity);
    if (!shouldApplyFrame(frameAt, lastAppliedAtRef.current)) return;
    lastAppliedAtRef.current = frameAt;
    setSignal(entity);
    setActive(turnActive(entity));
  });

  useEffect(() => {
    if (!active) return undefined;
    // One check at the expiry horizon is enough: any real iteration republishes and resets it.
    const timer = setTimeout(() => setActive(turnActive(signal)), TURN_STALE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [active, signal]);

  return active;
}
